import type { UseQueryOptions, UseQueryResult } from 'react-query';
import { useQuery } from 'react-query';
import type { SubscriptionStatus } from '../api/common/model/subscription';
import { subscriptionApi } from '../api';
import { getLoggedIn } from '../external-dependencies/webapp-utils';
import { CUSTOMER_SUMMARY_QUERY_KEY, SUBSCRIPTION_QUERY_CONFIG } from './config';

export interface AccountSubscriptionStatus {
  accountId: number;
  status: SubscriptionStatus | undefined;
}

/**
 * Load the status of the subscription belonging to the provided account id.
 *
 * @param accountId the account id.
 * @param [options] takes a subset of properties from `UseQueryOptions` - `enabled` and `select`.
 * @see UseQueryOptions
 */
export const useSubscriptionStatus = <TSelected = AccountSubscriptionStatus>(
  accountId: number | null | undefined,
  options?: Pick<
    UseQueryOptions<AccountSubscriptionStatus, Error, TSelected>,
    'enabled' | 'select'
  >,
): UseQueryResult<TSelected> => {
  const isLoggedIn = getLoggedIn();
  const hasAccountId = accountId != null;

  return useQuery<AccountSubscriptionStatus, Error, TSelected>(
    CUSTOMER_SUMMARY_QUERY_KEY,
    async (): Promise<AccountSubscriptionStatus> => {
      if (!hasAccountId) {
        // should never reach here if enabled is set correctly.
        return Promise.reject(Error('No account ID.'));
      }

      const customerSummary = await subscriptionApi.getCustomerSummary(accountId);

      return {
        accountId,
        status: customerSummary.subscriptionStatus,
      };
    },
    {
      ...SUBSCRIPTION_QUERY_CONFIG,
      enabled: isLoggedIn && hasAccountId && (options?.enabled ?? true),
      select: options?.select,
    },
  );
};
